<!--
This is the timeline bar for the Gen1 Performance Management experience. It should be deleted after
all clients are on the Gen3 experience (PM Milestone 3).
-->

<template>
    <!-- The inside of one BaseTabs #tab trigger, which owns the tab role, selection and the
         disabled state of a locked moment. The bar is positioned against that trigger. -->
    <div class="flex grow flex-col">
        <div class="flex flex-col items-center text-center gap-2 py-2 sm:flex-row sm:items-center sm:text-left sm:gap-2">
            <!-- Icon -->
            <div
                class="shrink-0 rounded-full flex items-center justify-center transition-colors h-6 w-6"
                :class="[state === 'completed' || state === 'open' ? 'bg-status-success-soft text-ink' : 'bg-surface-sunken text-ink-muted', { 'opacity-40': isDimmed }]">
                <Checkmark v-if="state === 'completed'" width="12" height="12" />
                <Clock v-else-if="state === 'open'" width="12" height="12" />
                <Archive v-else-if="state === 'expired'" width="12" height="12" />
                <LockSmall v-else width="12" height="12" />
            </div>

            <!-- Date and state text: stacked on mobile, inline on desktop -->
            <div class="min-w-0 w-full sm:w-auto sm:flex-1 details sm:body-small" :class="isDimmed ? 'text-ink-muted' : 'text-ink'">
                <span v-if="date" class="block sm:inline">{{ date }}</span>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: separator between date and state -->
                <span v-if="date" class="hidden sm:inline"> · </span>
                <span class="block sm:inline whitespace-nowrap">{{ stateDisplay }}</span>
            </div>
        </div>

        <div class="absolute -bottom-0.5 left-0 right-0 h-1.5 rounded-full" :class="isActive ? 'bg-tabs-indicator' : 'bg-tabs-hairline'"></div>
    </div>
</template>

<script setup lang="ts">
import Archive from "~vue/icons/Archive.vue";
import Checkmark from "~vue/icons/Checkmark.vue";
import Clock from "~vue/icons/Clock.vue";
import LockSmall from "~vue/icons/LockSmall.vue";
import type { PerformanceMomentState } from "~vue/types/performance_management";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    state: PerformanceMomentState;
    date?: string;
    isActive?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    date: "",
    isActive: false,
});

// In-progress ("open") moments must never look greyed out, even when another tab is
// selected — orgs can run multiple active moments at once (ENG-1330). Only inactive
// states (locked/completed/expired) are dimmed when unselected.
const isDimmed = computed(() => !props.isActive && props.state !== "open");

const stateDisplay = computed(() => {
    switch (props.state) {
        case "locked":
            return t("components.moments.state.closed");
        case "open":
            return t("components.moments.state.inProgress");
        case "completed":
            return t("components.moments.state.completed");
        case "expired":
            return t("components.moments.state.expired");
        default:
            return "";
    }
});
</script>
